export type SafeAreaInset = {
  /** Top safe area inset in pixels (notch, Dynamic Island, or status bar) */
  top: number;
  /** Bottom safe area inset in pixels (home indicator on Face ID devices) */
  bottom: number;
  /** Left safe area inset in pixels (rounded corners in landscape mode) */
  left: number;
  /** Right safe area inset in pixels (rounded corners in landscape mode) */
  right: number;
};

/**
 * @description
 * `getSafeAreaInset` is a utility function that returns all safe area insets in pixels as an object.
 *
 * This function reads the CSS `env(safe-area-inset-*)` values by creating
 * a temporary DOM element and reading its computed style.
 *
 * Safe area insets account for device-specific UI elements:
 * - **top**: Notch, Dynamic Island, or status bar
 * - **bottom**: Home indicator on Face ID devices
 * - **left/right**: Rounded corners in landscape mode
 *
 * Typical values (iPhone with Face ID, portrait mode):
 * - top: 47-59px (notch/Dynamic Island)
 * - bottom: 34px (home indicator)
 * - left/right: 0px
 *
 * @returns {SafeAreaInset} Object containing safe area insets for all four sides, or all 0 if not available.
 * - top `number` - Top safe area inset in pixels;
 * - bottom `number` - Bottom safe area inset in pixels;
 * - left `number` - Left safe area inset in pixels;
 * - right `number` - Right safe area inset in pixels;
 *
 * @example
 * const { top, bottom, left, right } = getSafeAreaInset();
 *
 * header.style.paddingTop = `${top}px`;
 * footer.style.paddingBottom = `${bottom}px`;
 */
export function getSafeAreaInset(): SafeAreaInset {
  const div = document.createElement("div");
  div.style.position = "fixed";
  div.style.visibility = "hidden";
  div.style.setProperty("padding-top", "env(safe-area-inset-top)");
  div.style.setProperty("padding-bottom", "env(safe-area-inset-bottom)");
  div.style.setProperty("padding-left", "env(safe-area-inset-left)");
  div.style.setProperty("padding-right", "env(safe-area-inset-right)");
  document.body.appendChild(div);

  const computedStyle = window.getComputedStyle(div);
  const inset: SafeAreaInset = {
    top: Number.parseFloat(computedStyle.getPropertyValue("padding-top")) || 0,
    bottom: Number.parseFloat(computedStyle.getPropertyValue("padding-bottom")) || 0,
    left: Number.parseFloat(computedStyle.getPropertyValue("padding-left")) || 0,
    right: Number.parseFloat(computedStyle.getPropertyValue("padding-right")) || 0,
  };

  document.body.removeChild(div);
  return inset;
}
